import React, { FC, useEffect, useState } from 'react';
import './App.css';
import { useLazyQuery } from '@apollo/client';
import { denthubTheme } from 'theme/base';
import { whitelabelTheme } from 'theme/whitelabel';
import { ThemeProvider, Theme, StyledEngineProvider } from '@mui/material/styles';
import App from 'App';
import { CONSTANTS } from 'config/constants';
import { getUserTemplate } from 'apollo/templates/user';
import { useLocation } from 'react-router-dom';
import { isSignedInVar, userIdVar } from 'apollo/reactive-vars';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { Organization } from 'models/organization';
import { socketIo } from 'context/socket';
import { Socket } from 'socket.io-client';
import { permissions } from './utils/permission';
import { logout, logoutOnSessionError } from './utils/logout';
import { CssBaseline } from '@mui/material';
import Cookies from 'js-cookie';
import { setErrorReportingUser } from 'error-reporting/error-reporting';
import {
    DenthubErrorBoundaryProvider,
    UserFrontendFeedbackDialog,
} from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

const { TOKEN_KEY, LAB_ID_KEY, COOKIE_OPTIONS } = CONSTANTS;

const AppContainer: FC = () => {
    // Iframe buster
    // eslint-disable-next-line no-restricted-globals
    if (top !== self) {
        // eslint-disable-next-line no-restricted-globals
        if (top) {
            // eslint-disable-next-line no-restricted-globals
            top.location = location as any;
        }
    }

    const search = useLocation().search;
    const [socketContext, setSocketContext] = useState<React.Context<Socket>>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [theme, setTheme] = useState<Theme>(denthubTheme);

    const [getUser] = useLazyQuery(getUserTemplate, {
        fetchPolicy: 'no-cache',
        onError: (err) => {
            logoutOnSessionError(err?.message);
        },
        onCompleted: (res) => {
            const user = Object.assign({}, res.user);
            currentOrgVar(user.organization ?? ({} as Organization));
            currentUserVar(user);
            userIdVar(res.user._id);
            isSignedInVar(true);
            setErrorReportingUser(user);

            socketIo.connectToWs();
            setSocketContext(socketIo.getSocketContext());
            if (permissions.isWhiteLabeledOrganization(res.user.organization)) {
                setTheme(whitelabelTheme);
            } else {
                setTheme(denthubTheme);
            }

            // Wait with 500ms to make sure that the socket connection is established
            setTimeout(() => {
                setIsLoading(false);
            }, 500);
        },
    });

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        const token = queryParams.get('token');
        const currentLabId = queryParams.get('labId');

        // Used for certificate API login.
        // After successful API login the JWT token is passed as query parameter.
        if (token) {
            Cookies.set(TOKEN_KEY, `JWT ${token as string}`, COOKIE_OPTIONS as Cookies.CookieAttributes);
        }

        // Lab used for white label
        if (currentLabId) {
            localStorage.setItem(LAB_ID_KEY, currentLabId);
        }
        if (Cookies.get(TOKEN_KEY)) {
            getUser();
        } else {
            logout(true);
        }
    }, []);

    return (
        <StyledEngineProvider injectFirst>
            {theme && !isLoading && socketContext ? (
                <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <socketContext.Provider value={socketIo.getSocket()}>
                        <DenthubErrorBoundaryProvider>
                            <App />
                            <UserFrontendFeedbackDialog />
                        </DenthubErrorBoundaryProvider>
                    </socketContext.Provider>
                </ThemeProvider>
            ) : undefined}
        </StyledEngineProvider>
    );
};

export default AppContainer;
